import classNames from 'classnames'
import React, { HTMLAttributes, ReactNode, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useStableId } from '../../utils'
import { useModalPortalContainer } from '../modal-base'
import { getTooltipPositionStyles } from './getTooltipPositionStyles'
import classes from './index.module.css'

interface Props extends HTMLAttributes<HTMLDivElement> {
  /**
   * The ID of an element to use for placement of the tooltip.
   */
  referenceId: string
  label: ReactNode
  isActive?: boolean
  offset?: { y: number; x: number }
  /**
   * The delay in ms before the tooltip is shown. Defaults to 500.
   */
  delay?: number
}

export const Tooltip = ({ isActive = true, referenceId, label, delay, ...props }: Props) => {
  const popperElement = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout>(undefined)
  const stableId = useStableId()
  const [showTooltip, setShowTooltip] = useState(false)
  const [onHover, setOnHover] = useState(false)
  const portalContainer = useModalPortalContainer()

  const onClose = () => {
    setShowTooltip(false)
    setOnHover(false)
    clearTimeout(timeoutRef.current)
  }

  const onOpen = () => {
    setOnHover(true)
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(true)
    }, delay ?? 500)
  }

  const menuStyles: React.CSSProperties = useMemo(() => {
    if (showTooltip && popperElement.current) {
      const referenceRect = document.getElementById(referenceId)?.getBoundingClientRect()
      return referenceRect
        ? getTooltipPositionStyles(
            popperElement.current.getBoundingClientRect(),
            referenceRect,
            document.body.getBoundingClientRect()
          )
        : { opacity: 0 }
    }
    return { opacity: 0 }
  }, [showTooltip])

  return (
    <div
      {...props}
      className={classNames(classes.wrapper, props.className)}
      onPointerEnter={onOpen}
      onPointerLeave={onClose}
      onFocus={onOpen}
      onBlur={onClose}
      aria-describedby={stableId}
    >
      {onHover &&
        createPortal(
          <div
            role="tooltip"
            id={stableId}
            ref={popperElement}
            style={menuStyles}
            className={classNames(showTooltip && isActive ? classes.showTooltip : '', classes.tooltip)}
          >
            {label}
          </div>,
          portalContainer
        )}

      {props.children}
    </div>
  )
}
